import { useEffect, useState } from 'react';

export type BookingsFilterValue = {
  q: string;
  services: string[];
  dateFrom: string;
  dateTo: string;
};

export const EMPTY_BOOKINGS_FILTER: BookingsFilterValue = {
  q: '',
  services: [],
  dateFrom: '',
  dateTo: '',
};

interface BookingsFilterBarProps {
  value: BookingsFilterValue;
  onChange: (next: BookingsFilterValue) => void;
  serviceOptions: { id: string; label: string }[];
  /** Placeholder text inside the search input. Defaults to a generic
   *  prompt so both customer + pro pages can label it appropriately
   *  (customer name vs pro name). */
  searchPlaceholder?: string;
}

// Live count of filter dimensions that are actually constraining the
// list. The search box is shown inline so it doesn't contribute.
function countActiveFilters(v: BookingsFilterValue): number {
  let n = 0;
  if (v.services.length > 0) n += 1;
  if (v.dateFrom) n += 1;
  if (v.dateTo) n += 1;
  return n;
}

export function BookingsFilterBar({
  value,
  onChange,
  serviceOptions,
  searchPlaceholder = 'Search by service, name or reference…',
}: BookingsFilterBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Pending state — what's shown inside the popup before Apply. Seeded
  // from `value` each time the popup opens so cancelling doesn't
  // accidentally retain partial edits.
  const [draft, setDraft] = useState<BookingsFilterValue>(value);

  useEffect(() => {
    if (isOpen) setDraft(value);
  }, [isOpen, value]);

  const activeCount = countActiveFilters(value);
  const serviceMap = new Map(serviceOptions.map((s) => [s.id, s.label]));

  const apply = () => {
    onChange(draft);
    setIsOpen(false);
  };
  const cancel = () => setIsOpen(false);

  const toggleService = (id: string) => {
    setDraft((d) => ({
      ...d,
      services: d.services.includes(id)
        ? d.services.filter((x) => x !== id)
        : [...d.services, id],
    }));
  };

  const removeServiceChip = (id: string) => {
    onChange({ ...value, services: value.services.filter((x) => x !== id) });
  };
  const clearDateFrom = () => onChange({ ...value, dateFrom: '' });
  const clearDateTo = () => onChange({ ...value, dateTo: '' });
  const clearAll = () =>
    onChange({ q: value.q, services: [], dateFrom: '', dateTo: '' });

  return (
    <div className="mb-6 space-y-3">
      {/* Search + filter button */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-[#908e8e] text-[20px] pointer-events-none">
            search
          </span>
          <input
            type="text"
            value={value.q}
            onChange={(e) => onChange({ ...value, q: e.target.value })}
            placeholder={searchPlaceholder}
            className="w-full h-[44px] pl-10 pr-10 rounded-[10px] border border-[#ede8e9] bg-white font-['DM_Sans',sans-serif] text-[14px] text-[#3a3335] placeholder:text-[#908e8e] focus:outline-none focus:border-[#8e2a2f] transition-colors"
          />
          {value.q ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onChange({ ...value, q: '' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-[#908e8e] hover:text-[#3a3335]"
            >
              <span className="material-icons text-[18px]">close</span>
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="relative h-[44px] px-4 rounded-[10px] border border-[#ede8e9] bg-white hover:border-[#8e2a2f] hover:text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[13px] text-[#3a3335] inline-flex items-center gap-2 transition-colors shrink-0"
        >
          <span className="material-icons text-[18px]">tune</span>
          Filter
          {activeCount > 0 ? (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#8e2a2f] text-white text-[11px] font-bold">
              {activeCount}
            </span>
          ) : null}
        </button>
      </div>

      {/* Active filter chips */}
      {activeCount > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {value.services.map((id) => (
            <FilterChip
              key={`svc-${id}`}
              label={`Service: ${serviceMap.get(id) ?? id}`}
              onRemove={() => removeServiceChip(id)}
            />
          ))}
          {value.dateFrom ? (
            <FilterChip
              label={`From ${value.dateFrom}`}
              onRemove={clearDateFrom}
            />
          ) : null}
          {value.dateTo ? (
            <FilterChip
              label={`To ${value.dateTo}`}
              onRemove={clearDateTo}
            />
          ) : null}
          <button
            type="button"
            onClick={clearAll}
            className="font-['DM_Sans',sans-serif] font-bold text-[12px] text-[#8e2a2f] hover:underline ml-1"
          >
            Clear all
          </button>
        </div>
      ) : null}

      {/* Filter modal */}
      {isOpen ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={cancel}
            aria-hidden
          />
          <div className="relative bg-white rounded-[14px] w-full max-w-[480px] max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#ede8e9]">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px]">
                Filters
              </h2>
              <button
                type="button"
                onClick={cancel}
                aria-label="Close filters"
                className="w-8 h-8 flex items-center justify-center text-[#908e8e] hover:text-[#3a3335]"
              >
                <span className="material-icons text-[20px]">close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Services */}
              <div>
                <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[12px] uppercase tracking-wider mb-2">
                  Service
                </p>
                {serviceOptions.length === 0 ? (
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
                    No services to filter by yet.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {serviceOptions.map((s) => {
                      const on = draft.services.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleService(s.id)}
                          className={`px-3 h-[32px] rounded-full border font-['DM_Sans',sans-serif] font-bold text-[12px] transition-colors ${
                            on
                              ? 'bg-[#8e2a2f] text-white border-[#8e2a2f]'
                              : 'bg-white text-[#3a3335] border-[#ede8e9] hover:border-[#8e2a2f] hover:text-[#8e2a2f]'
                          }`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Date range */}
              <div>
                <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[12px] uppercase tracking-wider mb-2">
                  Booking date range
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-1 block">
                      From
                    </span>
                    <input
                      type="date"
                      value={draft.dateFrom}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, dateFrom: e.target.value }))
                      }
                      className="w-full h-[40px] px-3 rounded-[8px] border border-[#ede8e9] bg-white font-['DM_Sans',sans-serif] text-[13px] text-[#3a3335] focus:outline-none focus:border-[#8e2a2f]"
                    />
                  </label>
                  <label className="block">
                    <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-1 block">
                      To
                    </span>
                    <input
                      type="date"
                      value={draft.dateTo}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, dateTo: e.target.value }))
                      }
                      className="w-full h-[40px] px-3 rounded-[8px] border border-[#ede8e9] bg-white font-['DM_Sans',sans-serif] text-[13px] text-[#3a3335] focus:outline-none focus:border-[#8e2a2f]"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#ede8e9] bg-[#fcfaf7]">
              <button
                type="button"
                onClick={cancel}
                className="h-[40px] px-5 rounded-[10px] border border-[#ede8e9] bg-white hover:border-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[13px] text-[#3a3335] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={apply}
                className="h-[40px] px-6 rounded-[10px] bg-[#8e2a2f] hover:bg-[#7a2428] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 h-[28px] pl-3 pr-1 rounded-full bg-[#fcfaf7] border border-[#ede8e9] font-['DM_Sans',sans-serif] text-[12px] font-bold text-[#3a3335]">
      {label}
      <button
        type="button"
        aria-label={`Remove filter ${label}`}
        onClick={onRemove}
        className="w-6 h-6 flex items-center justify-center text-[#908e8e] hover:text-[#8e2a2f]"
      >
        <span className="material-icons text-[16px]">close</span>
      </button>
    </span>
  );
}
